import { cn } from "../../lib/utils";

/**
 * Saved views — the named presets a list opens on.
 *
 * <p>A segmented row of `aria-pressed` buttons, NOT a `role="tablist"`: a view
 * changes which rows the list asks the server for, it does not switch between
 * panels already on the page, and a tab list would announce panels that do not
 * exist. It is also not `FilterChips` — a view is a preset over SEVERAL filters
 * at once (a status, a sort, an attention predicate), so rendering it in the
 * vocabulary of one filter would misdescribe what a click does.</p>
 *
 * <p>The optional `badge` is a count the host already has (a "needs action"
 * total, say). It renders only when supplied, never as a zero: "not counted"
 * and "none here" are different claims.</p>
 */
export interface SavedViewChipItem {
  key: string;
  label: string;
  badge?: number;
}

export interface SavedViewChipsProps {
  items: SavedViewChipItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Accessible name for the group, e.g. "Buyer views". */
  label?: string;
  className?: string;
  "data-testid"?: string;
}

export function SavedViewChips({
  items,
  activeKey,
  onSelect,
  label = "Saved views",
  className,
  "data-testid": testId,
}: SavedViewChipsProps) {
  return (
    <div
      className={cn("flex flex-wrap gap-space-2", className)}
      role="group"
      aria-label={label}
      data-testid={testId}
    >
      {items.map((item) => {
        const active = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(item.key)}
            data-testid={testId ? `${testId}-${item.key}` : undefined}
            className={cn(
              "inline-flex items-center gap-space-1 rounded-md border px-space-3 py-space-1 text-small",
              "transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:shadow-goldRing",
              active
                ? "border-onyx bg-onyx font-semibold text-white-pure"
                : "border-border-light bg-white-pure text-onyx hover:bg-white-warm",
            )}
          >
            {item.label}
            {item.badge != null && (
              // `text-label` (12px) is the smallest type token; the badge reads at that size.
              <span
                className={cn(
                  "rounded-pill px-space-1 text-label font-bold tabular-nums",
                  active ? "bg-white-pure/20 text-white-pure" : "bg-white-warm text-gray-mid",
                )}
              >
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
